import React, { useEffect, useState } from "react";
import axios from "axios";
import { Building2, RefreshCw, Receipt } from "lucide-react";
import { formatMsk } from "../../utils/timeUtils";
import { BUSINESS_STATUS_LABELS } from "../../components/b2b/B2BBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: "Открыто", discussing: "Обсуждается", accepted: "Принято", in_progress: "В работе", completed: "Ждёт подтверждения",
  confirmed: "Завершено", cancelled: "Отменено", disputed: "Спор", revision_requested: "Доработка", dispute_resolved_customer: "Спор: выбор заказчика", open: "Переоткрыто",
};

// N-8: действие админа, а не индикатор — последствие по номеру жалобы (репутационное правило)
const COMPLAINT_CONSEQUENCES = { 1: "предупреждение исполнителю", 2: "снятие бизнес-статуса на 30 дней", 3: "блокировка аккаунта исполнителя" };

const StatCard = ({ label, value, testId }) => (
  <div className="reversia-card p-4 text-center" data-testid={testId}>
    <div className="text-2xl font-bold" style={{ color: "#0369A1", fontFamily: "Manrope,sans-serif" }}>{value}</div>
    <div className="text-sm mt-1" style={{ color: "var(--rv-text-muted)" }}>{label}</div>
  </div>
);

export const B2BAdminTab = ({ showToast }) => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [complaintFor, setComplaintFor] = useState(null);
  const [complaintNote, setComplaintNote] = useState("");
  const [complaintAck, setComplaintAck] = useState(false);
  const [resending, setResending] = useState(null);

  // A4: повторная отправка пакета документов из админки
  const resend = async (taskId) => {
    if (resending) return;
    setResending(taskId);
    try {
      const r = await axios.post(`${API}/b2b/admin/tasks/${taskId}/documents/resend`);
      showToast?.(r.data?.message || "Пакет отправлен", "success");
    } catch (e) {
      showToast?.(e.response?.data?.detail || "Отправка не удалась", "error");
    } finally {
      setResending(null);
      load();
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/b2b/admin/stats`),
        axios.get(`${API}/b2b/admin/tasks`, { params: { status: statusFilter || undefined, limit: 50 } }),
      ]);
      setStats(s.data);
      setItems(t.data.items || []);
      setTotal(t.data.total || 0);
    } catch (e) {
      showToast(e.response?.data?.detail || "Не удалось загрузить B2B-данные", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const submitComplaint = async () => {
    try {
      const r = await axios.post(`${API}/b2b/admin/users/${complaintFor.id}/receipt-complaint`, { note: complaintNote });
      const map = { warning: "Вынесено предупреждение", status_reset: "Бизнес-статус снят на 30 дней", blocked: "Аккаунт заблокирован" };
      showToast(`${map[r.data.action] || r.data.action} (жалоб: ${r.data.warnings})`, "success");
      setComplaintFor(null);
      setComplaintNote("");
      load();
    } catch (e) {
      showToast(e.response?.data?.detail || "Ошибка", "error");
    }
  };

  const money = (v) => `${Number(v || 0).toLocaleString("ru-RU")} ₽`;

  return (
    <div data-testid="admin-b2b-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope,sans-serif" }}>
          <Building2 size={16} style={{ color: "#0369A1" }} /> B2B-задания (оплата напрямую, без эскроу)
        </h3>
        <button onClick={load} className="text-sm flex items-center gap-1" style={{ color: "var(--rv-text-muted)" }} data-testid="b2b-admin-refresh"><RefreshCw size={14} /> Обновить</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="B2B всего" value={stats.b2b_total} testId="b2b-stat-total" />
          <StatCard label="B2B активных" value={stats.b2b_active} testId="b2b-stat-active" />
          <StatCard label="B2B завершено" value={stats.b2b_completed} testId="b2b-stat-completed" />
          <StatCard label="B2B оборот (напрямую)" value={money(stats.b2b_turnover)} testId="b2b-stat-turnover" />
          <StatCard label="Исполнителей со статусом" value={stats.executors_with_status} testId="b2b-stat-executors" />
          <StatCard label="B2B-заказчиков" value={stats.b2b_customers} testId="b2b-stat-customers" />
          <StatCard label={`Комиссия (${stats.commission_enabled ? "включена" : "выключена"})`} value={money(stats.b2b_commission_accrued)} testId="b2b-stat-commission" />
          <StatCard label="Ставка при включении" value={`${stats.commission_percent}% + ${stats.commission_fixed} ₽`} testId="b2b-stat-rate" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm" style={{ color: "var(--rv-text-secondary)" }}>Статус:</span>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1" data-testid="b2b-admin-status-filter">
          <option value="">Все ({total})</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? <p className="text-sm" style={{ color: "var(--rv-text-muted)" }}>Загрузка...</p> : (
        <div className="overflow-x-auto bg-white rounded-lg" style={{ border: "1px solid var(--rv-border)" }}>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-500">
                <th className="px-3 py-2">Задание</th>
                <th className="px-3 py-2">Организация</th>
                <th className="px-3 py-2">Заказчик</th>
                <th className="px-3 py-2">Исполнитель</th>
                <th className="px-3 py-2">Цена</th>
                <th className="px-3 py-2">Статус</th>
                <th className="px-3 py-2">Документы</th>
                <th className="px-3 py-2">Создано</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">B2B-заданий нет</td></tr>}
              {items.map(t => (
                <tr key={t.id} className="border-t" data-testid={`b2b-admin-task-row-${t.id}`}>
                  <td className="px-3 py-2 align-top"><div className="font-medium">{t.title}</div><div className="text-xs text-gray-400">{t.id.slice(0, 8)}</div></td>
                  <td className="px-3 py-2 align-top"><div>{t.org_name}</div><div className="text-xs text-gray-400">ИНН {t.inn_masked}</div><div className="text-xs text-gray-400">{t.docs_email}</div></td>
                  <td className="px-3 py-2 align-top text-xs">{t.customer?.email || "—"}</td>
                  <td className="px-3 py-2 align-top text-xs">
                    {t.executor ? (
                      <div>
                        <div>{t.executor.email}</div>
                        <div className="text-emerald-700">{BUSINESS_STATUS_LABELS[t.executor.business_status] || "Не указан"}</div>
                        {t.executor.business_status_warnings > 0 && (
                          <div className="text-amber-700" data-testid={`b2b-receipt-complaints-count-${t.id}`}>Зарегистрировано жалоб на чек: {t.executor.business_status_warnings}</div>
                        )}
                        <button onClick={() => { setComplaintFor(t.executor); setComplaintAck(false); }} className="mt-1 text-[11px] flex items-center gap-1 px-2 py-0.5 rounded border"
                          style={{ color: "var(--rv-text-secondary)", borderColor: "var(--rv-border)", background: "#F8FAFC" }} data-testid={`b2b-receipt-complaint-btn-${t.id}`}>
                          <Receipt size={11} /> Зарегистрировать жалобу: чек не выдан
                        </button>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 align-top">{money(t.price)}</td>
                  <td className="px-3 py-2 align-top">{STATUS_LABELS[t.status] || t.status}</td>
                  <td className="px-3 py-2 align-top text-xs" data-testid={`b2b-docs-status-${t.id}`}>
                    {t.last_send_status === "failed" ? (
                      <div className="text-red-600">
                        <div className="font-medium">Сбой отправки</div>
                        <div className="text-[11px] break-words max-w-[180px]">{t.last_send_error}</div>
                      </div>
                    ) : t.documents_sent_at ? `Отправлены ${formatMsk(t.documents_sent_at)}` : "—"}
                    {t.status === "confirmed" && (
                      <button data-testid={`b2b-resend-docs-${t.id}`} disabled={resending === t.id} onClick={() => resend(t.id)}
                        className="mt-1 block text-[11px] underline disabled:opacity-50" style={{ color: "#0369A1" }}>
                        {resending === t.id ? "Отправляем..." : "Повторить отправку"}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-xs">{t.created_at ? formatMsk(t.created_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {complaintFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="b2b-receipt-complaint-modal">
            <h3 className="text-base font-semibold mb-1">Зарегистрировать жалобу: чек не выдан</h3>
            <p className="text-sm text-gray-500 mb-2">Исполнитель: {complaintFor.email}. Уже зарегистрировано жалоб: {complaintFor.business_status_warnings || 0}.</p>
            <p className="text-sm mb-2 p-2 rounded" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }} data-testid="b2b-receipt-complaint-consequence">
              Последствие этой жалобы: {COMPLAINT_CONSEQUENCES[Math.min((complaintFor.business_status_warnings || 0) + 1, 3)]}. Действие сразу применяется к аккаунту исполнителя и отправляет ему уведомление.
            </p>
            <p className="text-xs text-gray-500 mb-3">Правило: 1-я жалоба — предупреждение, 2-я — снятие бизнес-статуса на 30 дней, 3-я — блокировка аккаунта.</p>
            <label className="flex items-start gap-2 text-sm mb-3">
              <input type="checkbox" checked={complaintAck} onChange={e => setComplaintAck(e.target.checked)} className="mt-0.5" data-testid="b2b-receipt-complaint-ack" />
              Заказчик подтвердил, что чек по заданию не выдан
            </label>
            <textarea value={complaintNote} onChange={e => setComplaintNote(e.target.value)} rows={3} placeholder="Комментарий (задание, дата, суть)"
              className="w-full p-2 border border-gray-300 rounded text-sm mb-3" data-testid="b2b-receipt-complaint-note" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setComplaintFor(null)} className="px-3 py-1.5 text-sm rounded border">Отмена</button>
              <button onClick={submitComplaint} disabled={!complaintAck} className="px-3 py-1.5 text-sm rounded bg-red-600 text-white disabled:opacity-50" data-testid="b2b-receipt-complaint-submit">Зарегистрировать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
